import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Dahiana Marín Bienes Raíces — Propiedades en Paraguay" },
      {
        name: "description",
        content:
          "Portal inmobiliario de Dahiana Marín. Casas, departamentos y terrenos en venta y alquiler en Asunción y Central.",
      },
      { name: "author", content: "Dahiana Marín Bienes Raíces" },
      { property: "og:title", content: "Dahiana Marín Bienes Raíces — Propiedades en Paraguay" },
      { property: "og:description", content: "Portal inmobiliario de Dahiana Marín. Casas, departamentos y terrenos en venta y alquiler en Asunción y Central." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Dahiana Marín Bienes Raíces" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Dahiana Marín Bienes Raíces — Propiedades en Paraguay" },
      { name: "twitter:description", content: "Portal inmobiliario de Dahiana Marín. Casas, departamentos y terrenos en venta y alquiler en Asunción y Central." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/41b4b71f-593a-4a3d-bc0e-2cc3609dfcc3" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/41b4b71f-593a-4a3d-bc0e-2cc3609dfcc3" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
    ],
    scripts: [
      {
        // Analytics diferido: se carga tras la primera interacción o al quedar el navegador libre,
        // para no competir con el render inicial (LCP).
        children:
          "window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-8FT6N0YQGR');" +
          "(function(){var loaded=false;function load(){if(loaded)return;loaded=true;var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=G-8FT6N0YQGR';document.head.appendChild(s);}" +
          "['scroll','pointerdown','keydown','touchstart'].forEach(function(e){window.addEventListener(e,load,{once:true,passive:true});});" +
          "if('requestIdleCallback' in window){requestIdleCallback(load,{timeout:6000});}else{setTimeout(load,4000);}})();",
      },

      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "RealEstateAgent",
          "@id": "https://dahianamarin.com/#agencia",
          name: "Dahiana Marín Bienes Raíces",
          url: "https://dahianamarin.com/",
          image: "https://dahianamarin.com/favicon.png",
          logo: "https://dahianamarin.com/favicon.png",
          telephone: "+595986733480",
          email: "ventas@dahianamarin.com",
          priceRange: "$$",
          description:
            "Agente inmobiliaria en Paraguay. Propiedades en Asunción y Central: venta, alquiler y asesoría personalizada.",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Asunción",
            addressRegion: "Central",
            addressCountry: "PY",
          },
          areaServed: [
            { "@type": "Place", name: "Asunción" },
            { "@type": "Place", name: "Central" },
            { "@type": "Country", name: "Paraguay" },
          ],
          sameAs: ["https://www.instagram.com/dahianamarin.realestate"],
        }),
      },

    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <Toaster richColors position="top-right" />
    </QueryClientProvider>
  );
}
